import JsSdkBase from '../core/js-sdk-base';
import { FunctionKeys, SdkType } from '../interface';
import { captureSdkResult, sdkLogger } from '../utils';

/**
 * defaultImpl装饰器：自动检查并使用业务侧提供的默认实现
 * 如果 defaultImplementations 中存在对应方法的实现，则直接调用该实现
 * 否则执行原方法逻辑
 *
 * @example
 * ```typescript
 * @defaultImpl
 * openMap(params: any): Promise<any> {
 *     return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
 * }
 * ```
 *
 * @param _ 类的实例
 * @param propKey 类的属性名
 * @param descriptor 类的属性描述符
 */
export function defaultImpl(
    _: JsSdkBase,
    propKey: FunctionKeys<JsSdkBase>,
    descriptor: PropertyDescriptor
) {
    const originalMethod = descriptor.value;
    type OriginalMethodType = typeof originalMethod;

    descriptor.value = function (
        this: JsSdkBase,
        ...args: Parameters<OriginalMethodType>
    ): ReturnType<OriginalMethodType> {
        // 检查是否有业务侧提供的默认实现
        const defaultImpl = this.defaultImplementations.get(propKey);
        if (defaultImpl) {
            // 因为是给浏览器环境提供的默认实现，所以sdkType固定为browser
            sdkLogger.info(`browser ${propKey} default impl invoked`, { args });
            // 如果有默认实现，直接调用并返回
            const result = defaultImpl.apply(this, args);
            return captureSdkResult({
                result,
                sdkType: SdkType.Browser,
                sdkApi: propKey,
                prefix: 'default impl'
            });
        }

        // 否则执行原方法
        return originalMethod.apply(this, args);
    };

    return descriptor;
}
